import { useEffect, useMemo } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Home from '@/pages/Home.jsx';
import Sale from '@/pages/Sale.jsx';
import Rewards from '@/pages/Rewards.jsx';
import Settings from '@/pages/Settings.jsx';
import Login from '@/pages/Login.jsx';
import OAuthCallback from '@/pages/OAuthCallback.jsx';
import InvoicePayment from '@/pages/InvoicePayment.jsx';
import PrintPopup from '@/pages/PrintPopup.jsx';
import PrintWorker from '@/pages/PrintWorker.jsx';
import { CartProvider, useCart } from '@/context/CartContext.jsx';
import { Toaster } from '@/components/ui/toaster.jsx';
import { useAppLifecycle } from '@/hooks/useAppLifecycle.js';
import { initializeSessionValidation } from '@/utils/sessionValidator.js';
import { useLocationSync } from '@/hooks/useLocationSync.js';
import { BACKEND_URL } from '@/config';

function Shell({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const showBottomTotal = !['/settings','/login','/invoice-payment'].some(path => loc.pathname.includes(path));
  const { total } = useCart();
  useEffect(() => { document.title = 'Castle POS'; }, []);
  const canGoBack = !['/','/login'].includes(loc.pathname);
  
  // Check if running inside iframe (GHL SSO context)
  const isInIframe = window.self !== window.top;
  
  // Sync GHL location with backend session
  useLocationSync();
  return (
    <div>
      <div className="h-14 flex items-center justify-between px-4 border-b">
        <div className="flex items-center gap-2">
          {canGoBack && (
            <button data-testid="nav-back" onClick={() => nav(-1)} className="text-base px-3 py-2 border rounded hover:bg-muted active:scale-[0.99]">Back</button>
          )}
          <button onClick={() => nav('/')} className="text-base px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 active:scale-[0.99] font-semibold">Dashboard</button>
          <div className="font-semibold text-lg">Castle POS</div>
        </div>
        <button data-testid="sign-out" className="text-base px-3 py-2 border rounded hover:bg-muted" onClick={async ()=>{ 
          // Logout and redirect to login
          // In iframe: SSO will automatically re-authenticate
          // Outside iframe: User will see login page
          try{ 
            await fetch(`${BACKEND_URL}/api/auth/logout`, { method:'POST', credentials:'include' }); 
          }catch{} 
          localStorage.removeItem('pos_session_id'); // Clear session from localStorage
          window.location.href='/login';
        }}>
          {isInIframe ? 'Refresh' : 'Sign Out'}
        </button>
      </div>
      <main>{children}</main>
      {showBottomTotal && (
        <div className="fixed bottom-0 left-0 right-0 h-16 border-t bg-background flex items-center justify-end px-4">
          <div className="text-base">Cart Total:&nbsp;<span data-testid="bottom-cart-total" className="font-semibold">${total.toFixed(2)}</span></div>
        </div>
      )}
      <Toaster />
    </div>
  );
}

function RequireDailyLogin({ children }) {
  const today = useMemo(() => new Date().toISOString().slice(0,10), []);
  const last = localStorage.getItem('pos_session_id');
  
  // CRITICAL: Check if running in iframe
  const isInIframe = window.self !== window.top;
  
  // If NOT in iframe, always redirect to login (which will show access message)
  if (!isInIframe) {
    console.log('[Auth Guard] Not in iframe - redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  // In iframe: check daily login
  const loginDate = localStorage.getItem('pos_login_date');
  if (loginDate !== today) return <Navigate to="/login" replace />;
  
  return children;
}

function AppRoutes() {
  const loc = useLocation();
  const isPrintWorker = loc.pathname === '/print-worker' || loc.pathname === '/print';
  
  // Monitor app lifecycle (resume from background) and check session
  useAppLifecycle(isPrintWorker);
  
  // Initialize session validation for iframe context
  useEffect(() => {
    if (!isPrintWorker) {
      initializeSessionValidation();
    }
  }, [isPrintWorker]);
  
  return (
    <Routes>
      <Route path="/oauth/callback" element={<OAuthCallback />} />
      <Route path="/login" element={<Login />} />
      <Route path="/print" element={<PrintPopup />} />
      <Route path="/print-worker" element={<PrintWorker />} />
      <Route path="/" element={<RequireDailyLogin><Shell><Home /></Shell></RequireDailyLogin>} />
      <Route path="/sale" element={<RequireDailyLogin><Shell><Sale /></Shell></RequireDailyLogin>} />
      <Route path="/invoice-payment/:invoiceId" element={<RequireDailyLogin><Shell><InvoicePayment /></Shell></RequireDailyLogin>} />
      <Route path="/rewards" element={<RequireDailyLogin><Shell><Rewards /></Shell></RequireDailyLogin>} />
      <Route path="/settings" element={<RequireDailyLogin><Shell><Settings /></Shell></RequireDailyLogin>} />
    </Routes>
  );
}

export default function App() {
  return (
    <CartProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </CartProvider>
  );
}
